/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { isQueryLikeWorkItemKey } from '@atlassian/search-common/generate-search-url';

import { SearchResultSkeleton } from '../../../../../common/ui/quick-find/result-skeleton';

interface IntentRouterWorkItemProps {
	matchingItem?: React.ReactElement;
	query: string;
	isLoading?: boolean;
}

// Container styles for animation
const styles = cssMap({
	container: {
		overflow: 'hidden',
		transition: 'all 200ms ease-in-out',
		marginBottom: token('space.025'),
	},
});

const IntentRouterWorkItem = ({ matchingItem, query, isLoading }: IntentRouterWorkItemProps) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);
	const [displayContent, setDisplayContent] = useState<React.ReactElement | null>(null);
	const [maxHeight, setMaxHeight] = useState<string>('0px');

	// Update content when it changes
	useEffect(() => {
		const shouldShow = !!matchingItem || (isLoading && isQueryLikeWorkItemKey(query));

		if (shouldShow) {
			setDisplayContent(matchingItem || <SearchResultSkeleton />);
			setIsVisible(true);
		} else {
			setMaxHeight('0px'); // Start collapsed
			setIsVisible(false);
		}
	}, [matchingItem, isLoading, query]);

	// After content is mounted, measure and expand
	useEffect(() => {
		if (isVisible && displayContent) {
			const frameId = requestAnimationFrame(() => {
				if (contentRef.current) {
					setMaxHeight(`${contentRef.current.scrollHeight}px`);
				}
			});
			return () => cancelAnimationFrame(frameId);
		}
	}, [isVisible, displayContent]);

	// Unmount content after transition ends
	const handleTransitionEnd = () => {
		if (!isVisible && displayContent) {
			setDisplayContent(null);
		}
	};

	if (!isVisible && !displayContent) {
		return null;
	}

	return (
		<Box
			ref={contentRef}
			xcss={styles.container}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{
				maxHeight,
			}}
			onTransitionEnd={handleTransitionEnd}
		>
			{displayContent}
		</Box>
	);
};

export default IntentRouterWorkItem;
